
'use client';

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogClose } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { format } from "date-fns";
import { vi } from 'date-fns/locale';
import { CalendarIcon, Info, QrCode, UploadCloud, Image as ImageIcon, Phone, MessageSquare } from 'lucide-react';
import { cn, parseMessengerInput, parseZaloInput, parseLineInput } from '@/lib/utils';
import { JpFlagIcon, VnFlagIcon, ZaloIcon, MessengerIcon, LineIcon } from '../../../components/custom-icons';
import type { CandidateProfile } from '@/ai/schemas';
import { Alert, AlertDescription, AlertTitle } from '../../../components/ui/alert';
import { Badge } from '../../../components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import Image from 'next/image';
import { useIsMobile } from '@/hooks/use-mobile';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { validateProfileForApplication } from '@/lib/validators';
import { useAuth } from '@/contexts/AuthContext';
import { updateProfile } from '@/actions/user-action';


type EnrichedCandidateProfile = CandidateProfile & {
    avatarUrl?: string;
    videos?: any[];               // thêm nếu bạn lưu mảng url / object video
    images?: any[];               // thêm nếu bạn lưu mảng ảnh
    documents?: Record<string, any>; // thêm nếu có trường documents
    aspirations?: Record<string, any>; // thêm nếu có trường aspirations
};

interface EditProfileDialogProps {
    isOpen: boolean;
    onOpenChange: (isOpen: boolean) => void;
    onSaveSuccess: (updatedProfile: any) => void;
    onCancel?: () => void;
    source?: 'application' | 'profile';
}

const formatPhoneNumberInput = (value: string, country: string): string => {
    if (!value) return '';
    const cleanValue = value.replace(/\D/g, '');

    if (country === '+84') { // Vietnam (10 digits total)
        if (!cleanValue.startsWith('0')) return cleanValue.slice(0, 9);
        if (cleanValue.length === 0) return '';
        if (cleanValue.length === 1) return `(0)`;

        const mobilePart = cleanValue.substring(1);
        if (mobilePart.length <= 3) return `(0) ${mobilePart}`;
        if (mobilePart.length <= 6) return `(0) ${mobilePart.slice(0, 3)} ${mobilePart.slice(3)}`;
        return `(0) ${mobilePart.slice(0, 3)} ${mobilePart.slice(3, 6)} ${mobilePart.slice(6, 9)}`;
    }

    if (country === '+81') { // Japan (11 digits total starting with 0)
        if (!cleanValue.startsWith('0')) return cleanValue.slice(0, 10);
        if (cleanValue.length === 0) return '';
        if (cleanValue.length === 1) return `(0)`;

        const mobilePart = cleanValue.substring(1);
        if (mobilePart.length <= 2) return `(0)${mobilePart}`;
        if (mobilePart.length <= 6) return `(0)${mobilePart.slice(0, 2)} ${mobilePart.slice(2, 6)}`;
        return `(0)${mobilePart.slice(0, 2)} ${mobilePart.slice(2, 6)} ${mobilePart.slice(6, 10)}`;
    }

    return cleanValue;
};


const japaneseLevels = ["Chưa biết tiếng Nhật", "JLPT N5", "JLPT N4", "JLPT N3", "JLPT N2", "JLPT N1", "Kaiwa N5", "Kaiwa N4", "Kaiwa N3", "Kaiwa N2", "Kaiwa N1", "Trình độ tương đương N5", "Trình độ tương đương N4", "Trình độ tương đương N3", "Trình độ tương đương N2", "Trình độ tương đương N1"];


const renderLevel1Edit = (
    tempCandidate: EnrichedCandidateProfile,
    handleTempChange: (
        section: keyof EnrichedCandidateProfile | 'personalInfo',
        field: string,
        value: any
    ) => void,
    phoneCountry: string,
    setPhoneCountry: (value: string) => void,
    zaloCountry: string,
    setZaloCountry: (value: string) => void,
    onQrClick: () => void,
    isMobile: boolean,
    isDatePickerOpen: boolean,
    setIsDatePickerOpen: (open: boolean) => void,
    errors: { messenger?: string; line?: string },
    validateField: (field: 'messenger' | 'line', value: string) => void
) => {
    const height = parseInt(tempCandidate.personalInfo?.height || '0', 10);
    const weight = parseInt(tempCandidate.personalInfo?.weight || '0', 10);

    const handleDateSelect = (date: Date | undefined) => {
        handleTempChange('personalInfo', 'dateOfBirth', date ? format(date, 'yyyy-MM-dd') : '');
    };

    const CalendarComponent = () => (
        <Calendar
            mode="single"
            locale={vi}
            selected={tempCandidate.personalInfo.dateOfBirth ? new Date(tempCandidate.personalInfo.dateOfBirth) : undefined}
            onSelect={(date) => {
                handleDateSelect(date);
                if (isMobile) setIsDatePickerOpen(false); // Close sheet on select
            }}
            // initialFocus
            captionLayout="dropdown-buttons"
            fromYear={1950}
            toYear={new Date().getFullYear() - 16}
        />
    );

    return (
        <div className="space-y-4">
            <Alert>
                <Info className="h-4 w-4" />
                <AlertTitle className="font-bold">Lưu ý quan trọng</AlertTitle>
                <AlertDescription>
                    Cần nhập đủ thông tin cá nhân và ít nhất 1 phương thức liên lạc (Số điện thoại, Zalo, Messenger, Line...) để có thể sử dụng nút <Badge className="mx-1 bg-accent-orange text-white align-middle px-1.5 py-0.5 text-xs">Ứng tuyển</Badge> trên các tin tuyển dụng.
                </AlertDescription>
            </Alert>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
                <div className="space-y-2">
                    <Label>Họ và tên</Label>
                    <Input value={tempCandidate.personalInfo.fullName || ''} onChange={(e) => handleTempChange('personalInfo', 'fullName', e.target.value)} />
                </div>
                <div className="space-y-2">
                    <Label>Ngày sinh</Label>
                    {isMobile ? (
                        <Sheet open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
                            <SheetTrigger asChild>
                                <Button
                                    variant={"outline"}
                                    className={cn(
                                        "w-full justify-start text-left font-normal",
                                        !tempCandidate.personalInfo.dateOfBirth && "text-muted-foreground"
                                    )}
                                >
                                    <CalendarIcon className="mr-2 h-4 w-4" />
                                    {tempCandidate.personalInfo.dateOfBirth ? (
                                        format(new Date(tempCandidate.personalInfo.dateOfBirth), "dd/MM/yyyy")
                                    ) : (
                                        <span>Chọn ngày sinh</span>
                                    )}
                                </Button>
                            </SheetTrigger>
                            <SheetContent side="bottom" className="h-auto">
                                <SheetHeader>
                                    <SheetTitle>Chọn ngày sinh</SheetTitle>
                                </SheetHeader>
                                <CalendarComponent />
                            </SheetContent>
                        </Sheet>
                    ) : (
                        <Popover>
                            <PopoverTrigger asChild>
                                <Button
                                    variant={"outline"}
                                    className={cn(
                                        "w-full justify-start text-left font-normal",
                                        !tempCandidate.personalInfo.dateOfBirth && "text-muted-foreground"
                                    )}
                                >
                                    <CalendarIcon className="mr-2 h-4 w-4" />
                                    {tempCandidate.personalInfo.dateOfBirth ? (
                                        format(new Date(tempCandidate.personalInfo.dateOfBirth), "dd/MM/yyyy")
                                    ) : (
                                        <span>Chọn ngày sinh</span>
                                    )}
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0">
                                <CalendarComponent />
                            </PopoverContent>
                        </Popover>
                    )}
                </div>
                <div className="space-y-2">
                    <Label>Giới tính</Label>
                    <Select value={tempCandidate.personalInfo.gender || ''} onValueChange={value => handleTempChange('personalInfo', 'gender', value)}>
                        <SelectTrigger><SelectValue placeholder="Chọn giới tính" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="Nam">Nam</SelectItem>
                            <SelectItem value="Nữ">Nữ</SelectItem>
                            <SelectItem value="Khác">Khác</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div className="space-y-2">
                    <Label htmlFor="japaneseProficiency">Năng lực tiếng Nhật</Label>
                    <Select value={tempCandidate.personalInfo.japaneseProficiency || ''} onValueChange={value => handleTempChange('personalInfo', 'japaneseProficiency', value)}>
                        <SelectTrigger id="japaneseProficiency">
                            <SelectValue placeholder="Chọn trình độ tiếng Nhật" />
                        </SelectTrigger>
                        <SelectContent>
                            {japaneseLevels.map(level => <SelectItem key={level} value={level}>{level}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div className="space-y-2">
                    <div className="flex justify-between items-center">
                        <Label>Chiều cao (cm)</Label>
                        <span className={cn("text-sm font-semibold", height > 0 ? "text-primary" : "text-muted-foreground")}>
                            {height > 0 ? `${height} cm` : 'Chưa chọn'}
                        </span>
                    </div>
                    <Slider
                        value={[height]}
                        onValueChange={([value]) => handleTempChange('personalInfo', 'height', String(value))}
                        min={140}
                        max={205}
                        step={1}
                    />
                </div>
                <div className="space-y-2">
                    <div className="flex justify-between items-center">
                        <Label>Cân nặng (kg)</Label>
                        <span className={cn("text-sm font-semibold", weight > 0 ? "text-primary" : "text-muted-foreground")}>
                            {weight > 0 ? `${weight} kg` : 'Chưa chọn'}
                        </span>
                    </div>
                    <Slider
                        value={[weight]}
                        onValueChange={([value]) => handleTempChange('personalInfo', 'weight', String(value))}
                        min={40}
                        max={120}
                        step={1}
                    />
                </div>
                <div className="space-y-2">
                    <Label>Hình xăm</Label>
                    <Select value={tempCandidate.personalInfo.tattooStatus || ''} onValueChange={value => handleTempChange('personalInfo', 'tattooStatus', value)}>
                        <SelectTrigger><SelectValue placeholder="Chọn tình trạng hình xăm" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="Không có">Không có</SelectItem>
                            <SelectItem value="Có xăm nhỏ (kín)">Có xăm nhỏ (kín)</SelectItem>
                            <SelectItem value="Có xăm to (lộ)">Có xăm to (lộ)</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div className="space-y-2">
                    <Label>Viêm gan B</Label>
                    <Select value={tempCandidate.personalInfo.hepatitisBStatus || ''} onValueChange={value => handleTempChange('personalInfo', 'hepatitisBStatus', value)}>
                        <SelectTrigger><SelectValue placeholder="Chọn tình trạng viêm gan B" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="Không viêm gan B">Không viêm gan B</SelectItem>
                            <SelectItem value="Viêm gan B thể tĩnh">Viêm gan B thể tĩnh</SelectItem>
                            <SelectItem value="Viêm gan B thể động">Viêm gan B thể động</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div className="md:col-span-2 mt-6 pt-6 border-t">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="phone" className="flex items-center gap-2">
                                <Image src="/img/phone.svg" alt="Phone" width={20} height={20} />
                                Số điện thoại
                            </Label>
                            <div className="flex items-center">
                                <Select value={phoneCountry} onValueChange={setPhoneCountry}>
                                    <SelectTrigger className="w-[120px] rounded-r-none">
                                        <SelectValue>
                                            <div className="flex items-center gap-1">
                                                <span>{phoneCountry === '+84' ? 'VN' : 'JP'}</span>
                                                <span>{phoneCountry}</span>
                                            </div>
                                        </SelectValue>
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="+84"><div className="flex items-center gap-2">VN (+84)</div></SelectItem>
                                        <SelectItem value="+81"><div className="flex items-center gap-2">JP (+81)</div></SelectItem>
                                    </SelectContent>
                                </Select>
                                <Input id="phone" type="tel" placeholder={phoneCountry === '+84' ? '(0) 901 234 567' : '(0)90 1234 5678'} className="rounded-l-none" value={formatPhoneNumberInput(tempCandidate.personalInfo.phone || '', phoneCountry)} onChange={e => handleTempChange('personalInfo', 'phone', e.target.value.replace(/\D/g, ''))} />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="zalo" className="flex items-center gap-2">
                                <ZaloIcon />
                                Zalo (Số điện thoại)
                            </Label>
                            <div className="flex items-center relative">
                                <Select value={zaloCountry} onValueChange={setZaloCountry}>
                                    <SelectTrigger className="w-[120px] rounded-r-none">
                                        <SelectValue>
                                            <div className="flex items-center gap-1">
                                                <span>{zaloCountry === '+84' ? 'VN' : 'JP'}</span>
                                                <span>{zaloCountry}</span>
                                            </div>
                                        </SelectValue>
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="+84"><div className="flex items-center gap-2">VN (+84)</div></SelectItem>
                                        <SelectItem value="+81"><div className="flex items-center gap-2">JP (+81)</div></SelectItem>
                                    </SelectContent>
                                </Select>
                                <Input id="zalo" placeholder={zaloCountry === '+84' ? '(0) 901 234 567' : '(0)90 1234 5678'} className="rounded-l-none" value={formatPhoneNumberInput(tempCandidate.personalInfo.zalo || '', zaloCountry)} onChange={(e) => handleTempChange('personalInfo', 'zalo', e.target.value.replace(/\D/g, ''))} />
                                <div onClick={onQrClick} className="absolute right-2 cursor-pointer text-muted-foreground hover:text-primary">
                                    <QrCode className="h-5 w-5" />
                                </div>
                            </div>
                        </div>
                        <div className="space-y-1">
                            <Label htmlFor="messenger" className="flex items-center gap-2"><MessengerIcon />Facebook Messenger</Label>
                            <Input
                                id="messenger"
                                placeholder="Dán link Facebook / Messenger hoặc username"
                                value={tempCandidate.personalInfo.messenger || ''}
                                onChange={(e) => handleTempChange('personalInfo', 'messenger', e.target.value)}
                                onBlur={(e) => validateField('messenger', e.target.value)}
                                className={cn(errors.messenger && "border-destructive")}
                            />
                            {!errors.messenger && <p className="text-xs text-muted-foreground">Hệ thống sẽ tự động lấy username của bạn.</p>}
                            {errors.messenger && <p className="text-xs text-destructive">{errors.messenger}</p>}
                        </div>
                        <div className="space-y-1">
                            <Label htmlFor="line" className="flex items-center gap-2"><LineIcon />Line</Label>
                            <Input
                                id="line"
                                placeholder="Dán link Line hoặc nhập ID của bạn"
                                value={tempCandidate.personalInfo.line || ''}
                                onChange={(e) => handleTempChange('personalInfo', 'line', e.target.value)}
                                onBlur={(e) => validateField('line', e.target.value)}
                                className={cn(errors.line && "border-destructive")}
                            />
                            {!errors.line && <p className="text-xs text-muted-foreground">Hệ thống sẽ tự động lấy username của bạn.</p>}
                            {errors.line && <p className="text-xs text-destructive">{errors.line}</p>}
                        </div>
                    </div>
                    <div className="mt-4 text-center text-sm">
                        <div className="text-muted-foreground">
                            Cung cấp ít nhất một phương thức liên lạc để <Badge className="mx-1 bg-accent-orange text-white align-middle px-1.5 py-0.5 text-xs">Ứng tuyển</Badge>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export function EditProfileDialog({ isOpen, onOpenChange, onSaveSuccess, source = 'profile', onCancel }: EditProfileDialogProps) {
    const { toast } = useToast();
    const { role, user } = useAuth();
    const [tempCandidate, setTempCandidate] = useState<EnrichedCandidateProfile | null>(null);
    const [phoneCountry, setPhoneCountry] = useState('+84');
    const [zaloCountry, setZaloCountry] = useState('+84');
    const [isQrDialogOpen, setIsQrDialogOpen] = useState(false);
    const [qrImagePreview, setQrImagePreview] = useState<string | null>(null);
    const isMobile = useIsMobile();
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
    const [isConfirmCancelOpen, setIsConfirmCancelOpen] = useState(false);
    const [isConfirmSaveOpen, setIsConfirmSaveOpen] = useState(false);
    const [errors, setErrors] = useState<{ messenger?: string, line?: string }>({});


    useEffect(() => {
        if (isOpen) {
            const emptyUser = {
                headline: '',
                location: '',
                about: '',
                education: [],
                experience: [],
                personalInfo: {},
                skills: [],
                interests: [],
                certifications: [],
                documents: {},
                desiredIndustry: '',
                aspirations: {},
                notes: '',
                videos: [],
                images: [],
            };
            if (user) {
                setTempCandidate({
                    ...emptyUser,
                    ...user
                });
            } else {
                setTempCandidate({
                    headline: '',
                    location: '',
                    about: '',
                    education: [],
                    experience: [],
                    personalInfo: {},
                    skills: [],
                    interests: [],
                    certifications: [],
                    documents: {},
                    desiredIndustry: '',
                    aspirations: {},
                    notes: '',
                    videos: [],
                    images: [],
                });
            }
            setErrors({});
        }
    }, [isOpen]);

    const validateField = (field: 'messenger' | 'line', value: string) => {
        if (!value) {
            setErrors(prev => ({ ...prev, [field]: undefined }));
            return true;
        }

        let isValid = false;
        let errorMessage = "Định dạng không hợp lệ.";

        if (field === 'messenger') {
            isValid = /^(https?:\/\/(www\.)?(facebook|m)\.com\/|m\.me\/|[\w.]{5,})/.test(value);
            errorMessage = "Vui lòng nhập link Facebook/Messenger hoặc username hợp lệ.";
        } else if (field === 'line') {
            isValid = /^(https?:\/\/line\.me\/|@?[\w.-]+)/.test(value);
            errorMessage = "Vui lòng nhập link Line hoặc Line ID hợp lệ.";
        }

        if (isValid) {
            setErrors(prev => ({ ...prev, [field]: undefined }));
        } else {
            setErrors(prev => ({ ...prev, [field]: errorMessage }));
        }
        return isValid;
    };


    const forceSave = async () => {
        if (tempCandidate) {
            const finalCandidate = { ...tempCandidate };
            if (finalCandidate.personalInfo.messenger) {
                finalCandidate.personalInfo.messenger = parseMessengerInput(finalCandidate.personalInfo.messenger);
            }
            if (finalCandidate.personalInfo.line) {
                finalCandidate.personalInfo.line = parseLineInput(finalCandidate.personalInfo.line);
            }
            if (finalCandidate.personalInfo.zalo) {
                finalCandidate.personalInfo.zalo = parseZaloInput(finalCandidate.personalInfo.zalo);
            }
            console.log(finalCandidate)

            const res = await updateProfile(user?.uid, {
                personalInfo: finalCandidate.personalInfo
            });
            localStorage.setItem('generatedCandidateProfile', JSON.stringify(finalCandidate));
            if (!!user) {
                user.personalInfo = finalCandidate.personalInfo;
            }
            onSaveSuccess(finalCandidate);
            onOpenChange(false);
            setIsConfirmSaveOpen(false);
        }
    }

    const handleSave = () => {
        if (!tempCandidate) return;

        const isMessengerValid = validateField('messenger', tempCandidate.personalInfo.messenger || '');
        const isLineValid = validateField('line', tempCandidate.personalInfo.line || '');

        if (!isMessengerValid || !isLineValid) {
            toast({
                variant: 'destructive',
                title: 'Thông tin không hợp lệ',
                description: 'Vui lòng sửa các lỗi được hiển thị trước khi lưu.',
            });
            return;
        }


        if (source === 'application') {
            const missingFields = validateProfileForApplication(tempCandidate);
            if (missingFields.length > 0) {
                setIsConfirmSaveOpen(true);
                return;
            }
        }

        forceSave();
    };

    const handleCancel = () => {
        if (source === 'application') {
            setIsConfirmCancelOpen(true);
        } else if (onCancel) {
            onCancel();
        } else {
            onOpenChange(false);
        }
    };

    const handleTempChange = (
        section: keyof EnrichedCandidateProfile | 'personalInfo' | 'aspirations' | 'documents',
        field: any,
        value?: any
    ) => {
        setTempCandidate(prev => {
            if (!prev) return null;
            const newCandidate = JSON.parse(JSON.stringify(prev)); // Deep copy

            // Nếu section là tên trường đơn lẻ và value được truyền => gán value
            if (section === 'personalInfo') {
                newCandidate.personalInfo = { ...newCandidate.personalInfo, [field]: value };
            } else if (section === 'aspirations' || section === 'documents') {
                // nếu muốn hỗ trợ cập nhật object nested
                newCandidate[section] = { ...(newCandidate as any)[section], [field]: value };
            } else {
                // Ở đây field có thể là toàn bộ giá trị thay thế cho section (ví dụ set array)
                (newCandidate as any)[section] = value === undefined ? field : value;
            }

            return newCandidate;
        });
    };

    const handleZaloQrUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (event) => {
                setQrImagePreview(event.target?.result as string);

                // --- SIMULATION of QR Code scan ---
                setTimeout(() => {
                    const mockPhoneNumber = '0912345678';
                    handleTempChange('personalInfo', 'zalo', mockPhoneNumber);
                    toast({
                        title: "Quét QR thành công",
                        description: `Số Zalo đã được cập nhật thành: ${mockPhoneNumber}`,
                    });
                    setIsQrDialogOpen(false);
                    setQrImagePreview(null); // Reset preview
                }, 1500); // Simulate processing time
                // --- END SIMULATION ---
            };
            reader.readAsDataURL(file);
        }
    };

    if (!tempCandidate) return null;

    return (
        <>
            <Dialog open={isOpen} onOpenChange={onOpenChange}>
                <DialogContent className="sm:max-w-3xl" id="DIENTHONGTINCANHAN01">
                    <DialogHeader>
                        <DialogTitle className="font-headline text-2xl">Chỉnh sửa Thông tin Cá nhân</DialogTitle>
                        <DialogDescription>Cập nhật thông tin của bạn để nhà tuyển dụng có thể liên hệ.</DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-4 py-4 max-h-[70vh] overflow-y-auto pr-4">
                        {renderLevel1Edit(
                            tempCandidate,
                            handleTempChange as any,
                            phoneCountry, setPhoneCountry,
                            zaloCountry, setZaloCountry,
                            () => setIsQrDialogOpen(true),
                            isMobile,
                            isDatePickerOpen,
                            setIsDatePickerOpen,
                            errors,
                            validateField
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={handleCancel}>Hủy</Button>
                        <Button type="button" onClick={handleSave} className="bg-primary text-white">
                            Lưu thay đổi
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <AlertDialog open={isConfirmCancelOpen} onOpenChange={setIsConfirmCancelOpen}>
                <AlertDialogContent id="UNGTUYEN-L02-HUY01">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Bạn chắc chắn muốn hủy?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Hồ sơ của bạn vẫn cần thêm thông tin để có thể ứng tuyển. Bạn có muốn dừng việc cập nhật lúc này không?
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Ở lại</AlertDialogCancel>
                        <AlertDialogAction onClick={() => { setIsConfirmCancelOpen(false); onOpenChange(false); }}>Vẫn hủy</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog open={isConfirmSaveOpen} onOpenChange={setIsConfirmSaveOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Lưu hồ sơ chưa hoàn chỉnh?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Bạn vẫn chưa điền đủ dữ liệu để Ứng tuyển, bạn chắc chắn muốn lưu chứ?
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Tiếp tục sửa</AlertDialogCancel>
                        <AlertDialogAction onClick={forceSave}>Vẫn lưu</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <Dialog open={isQrDialogOpen} onOpenChange={setIsQrDialogOpen}>
                <DialogContent className="sm:max-w-xl">
                    <DialogHeader>
                        <DialogTitle className="font-headline text-2xl">Tải lên mã QR Zalo</DialogTitle>
                        <DialogDescription>
                            Tải lên ảnh mã QR của bạn để tự động điền số điện thoại Zalo.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4 space-y-6">
                        <Label
                            htmlFor="zalo-qr-file-input"
                            className="relative flex flex-col items-center justify-center w-full h-64 px-6 pt-5 pb-6 border-2 border-dashed rounded-md cursor-pointer border-border hover:border-primary transition-colors bg-secondary/50"
                        >
                            {qrImagePreview ? (
                                <Image
                                    src={qrImagePreview}
                                    alt="Xem trước QR"
                                    fill
                                    className="object-contain rounded-md"
                                />
                            ) : (
                                <div className="space-y-2 text-center">
                                    <UploadCloud className="w-12 h-12 mx-auto text-muted-foreground" />
                                    <p className="font-semibold text-foreground">
                                        Nhấp hoặc kéo thả file vào đây
                                    </p>
                                    <p className="text-xs text-muted-foreground">PNG, JPG</p>
                                </div>
                            )}
                            <Input
                                id="zalo-qr-file-input"
                                type="file"
                                className="sr-only"
                                accept="image/png, image/jpeg"
                                onChange={handleZaloQrUpload}
                            />
                        </Label>

                        <div className="space-y-3">
                            <h4 className="font-semibold text-center">Các bước lấy mã QR trên Zalo</h4>
                            {/* Placeholder for instruction images */}
                            <div className="p-8 text-center bg-gray-100 rounded-md">
                                <p className="text-sm text-gray-500">Khu vực hiển thị ảnh hướng dẫn sẽ được thêm vào sau.</p>
                            </div>
                        </div>

                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}

